import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, API } from '@/lib/api';
import { ZONES } from '@/lib/quizData';
import { Input } from '@/components/ui/input';
import {
  Search, Filter, Download, LogOut, ShieldCheck, Users, MessageCircle, MousePointerClick, ArrowRight, GraduationCap,
  Trash2, TrendingUp
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, Cell
} from 'recharts';
import { toast } from 'sonner';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total_leads: 0, cta_clicks: 0, whatsapp_clicks: 0 });
  const [analytics, setAnalytics] = useState({ series: [], zones: [], total_in_window: 0, window_days: 30 });
  const [analyticsRange, setAnalyticsRange] = useState(30);
  const [q, setQ] = useState('');
  const [grade, setGrade] = useState('');
  const [zone, setZone] = useState('');
  const [clarity, setClarity] = useState('');

  useEffect(() => {
    if (!localStorage.getItem('cq_admin_token')) {
      navigate('/admin/login');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('cq_admin_token')) return;
    fetchAnalytics(analyticsRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyticsRange]);

  const fetchAnalytics = async (days) => {
    try {
      const res = await api.get('/admin/analytics/daily', { params: { days } });
      setAnalytics(res.data || { series: [], zones: [], total_in_window: 0, window_days: days });
    } catch (e) {
      // Non-fatal — chart just won't render.
    }
  };

  const fetchData = async (filters = {}) => {
    setLoading(true);
    try {
      const params = {};
      if (filters.q !== undefined ? filters.q : q) params.q = filters.q !== undefined ? filters.q : q;
      if (filters.grade !== undefined ? filters.grade : grade) params.grade = filters.grade !== undefined ? filters.grade : grade;
      if (filters.zone !== undefined ? filters.zone : zone) params.zone = filters.zone !== undefined ? filters.zone : zone;
      if (filters.clarity !== undefined ? filters.clarity : clarity) params.clarity = filters.clarity !== undefined ? filters.clarity : clarity;

      const [leadsRes, statsRes] = await Promise.all([
        api.get('/admin/leads', { params }),
        api.get('/admin/stats'),
      ]);
      setLeads(leadsRes.data.leads || []);
      setStats(statsRes.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem('cq_admin_token');
        navigate('/admin/login');
      } else {
        toast.error('Could not load leads');
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('cq_admin_token');
    localStorage.removeItem('cq_admin_user');
    navigate('/admin/login');
  };

  const exportCSV = async () => {
    try {
      const token = localStorage.getItem('cq_admin_token');
      const res = await fetch(`${API}/admin/leads/export/csv`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `career_quest_leads.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not export CSV');
    }
  };

  const handleApplyFilters = () => fetchData();
  const clearFilters = () => {
    setQ(''); setGrade(''); setZone(''); setClarity('');
    fetchData({ q: '', grade: '', zone: '', clarity: '' });
  };

  const purgeTestLeads = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete all TEST-tagged leads (names starting with "TEST" or example.com emails)? This cannot be undone.')) return;
    try {
      const res = await api.post('/admin/leads/purge-test');
      const n = res.data?.deleted || 0;
      toast.success(n ? `Deleted ${n} test lead${n === 1 ? '' : 's'}` : 'No test leads to purge');
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Purge failed');
    }
  };

  const zoneList = useMemo(() => Object.values(ZONES), []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <nav className="sticky top-0 z-30 bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-2xl bg-[#0055FF] text-white flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display font-extrabold text-ink text-sm">Career Fit Assessment · Admin</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-ink">Future Ready School</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/courses" data-testid="admin-courses-link" className="btn-secondary inline-flex items-center gap-2 !py-2.5 !px-4 text-sm">
              <GraduationCap className="w-4 h-4" strokeWidth={3} /> Coursera Links
            </Link>
            <button onClick={exportCSV} data-testid="admin-export-csv-button" className="btn-secondary inline-flex items-center gap-2 !py-2.5 !px-4 text-sm">
              <Download className="w-4 h-4" strokeWidth={3} /> Export CSV
            </button>
            <button onClick={purgeTestLeads} data-testid="admin-purge-test-leads" className="inline-flex items-center gap-2 !py-2.5 !px-4 text-sm rounded-full font-bold bg-[#FEE2E2] text-[#B91C1C] hover:bg-[#FECACA] transition-colors">
              <Trash2 className="w-4 h-4" strokeWidth={3} /> Purge Test Leads
            </button>
            <button onClick={logout} data-testid="admin-logout-button" className="text-sm font-bold text-muted-ink hover:text-ink inline-flex items-center gap-1.5">
              <LogOut className="w-4 h-4" strokeWidth={2.5} /> Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-5 py-8 space-y-6">
        <div>
          <h1 className="font-display font-black text-ink text-3xl sm:text-4xl tracking-tight">Leads Dashboard</h1>
          <p className="text-sm text-muted-ink mt-1">All Career Fit Assessment submissions in one place.</p>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-4">
          <StatCard icon={Users} label="Total submissions" value={stats.total_leads} color="#0055FF" />
          <StatCard icon={MousePointerClick} label="CTA clicks" value={stats.cta_clicks} color="#FF5A00" />
          <StatCard icon={MessageCircle} label="WhatsApp clicks" value={stats.whatsapp_clicks} color="#00D95F" />
        </div>

        {/* Analytics — daily completions + zone distribution */}
        <AnalyticsPanel
          analytics={analytics}
          range={analyticsRange}
          onRangeChange={setAnalyticsRange}
        />

        {/* Filters */}
        <div className="card-soft p-5">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-[#0055FF]" strokeWidth={3} />
            <div className="font-display font-extrabold text-ink">Filters</div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="lg:col-span-2 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-ink" />
              <Input
                data-testid="admin-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                placeholder="Search name, email, city, phone…"
                className="pl-9 h-11 rounded-2xl"
              />
            </div>
            <select data-testid="admin-grade-filter" value={grade} onChange={(e) => setGrade(e.target.value)} className="h-11 rounded-2xl border border-[#E2E8F0] px-3 text-sm bg-white font-bold">
              <option value="">All grades</option>
              {['8', '9', '10', '11', '12'].map((g) => <option key={g} value={g}>Grade {g}</option>)}
            </select>
            <select data-testid="admin-zone-filter" value={zone} onChange={(e) => setZone(e.target.value)} className="h-11 rounded-2xl border border-[#E2E8F0] px-3 text-sm bg-white font-bold">
              <option value="">All career zones</option>
              {zoneList.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
            </select>
            <select data-testid="admin-clarity-filter" value={clarity} onChange={(e) => setClarity(e.target.value)} className="h-11 rounded-2xl border border-[#E2E8F0] px-3 text-sm bg-white font-bold">
              <option value="">All clarity levels</option>
              <option value="1">1 — Very confused</option>
              <option value="2">2 — Some ideas</option>
              <option value="3">3 — 2-3 options</option>
              <option value="4">4 — Clear, wants validation</option>
            </select>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleApplyFilters} data-testid="admin-apply-filters" className="btn-primary !py-2.5 !px-5 text-sm">Apply</button>
            <button onClick={clearFilters} data-testid="admin-clear-filters" className="btn-secondary !py-2.5 !px-5 text-sm">Clear</button>
          </div>
        </div>

        {/* Table */}
        <div className="card-soft overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-leads-table">
              <thead className="bg-[#F8FAFC]">
                <tr className="text-left text-[10px] uppercase tracking-[0.2em] font-bold text-muted-ink">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Student</th>
                  <th className="px-4 py-3">Grade</th>
                  <th className="px-4 py-3">Parent</th>
                  <th className="px-4 py-3">WhatsApp</th>
                  <th className="px-4 py-3">City</th>
                  <th className="px-4 py-3">Avatar</th>
                  <th className="px-4 py-3">Primary Zone</th>
                  <th className="px-4 py-3">Guidance</th>
                  <th className="px-4 py-3">CTA</th>
                  <th className="px-4 py-3">WA</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={12} className="p-8 text-center text-muted-ink">Loading…</td></tr>
                ) : leads.length === 0 ? (
                  <tr><td colSpan={12} className="p-12 text-center">
                    <div className="font-display font-extrabold text-ink text-lg">No leads yet</div>
                    <div className="text-muted-ink text-sm mt-1">Submissions will appear here as students complete the quiz.</div>
                  </td></tr>
                ) : leads.map((l) => (
                  <tr key={l.id} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]">
                    <td className="px-4 py-3 text-muted-ink whitespace-nowrap">{(l.created_at || '').slice(0, 10)}</td>
                    <td className="px-4 py-3 font-display font-extrabold text-ink">{l.student_name}</td>
                    <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-xs font-bold"><GraduationCap className="w-3.5 h-3.5 text-[#0055FF]" strokeWidth={3} /> {l.grade}</span></td>
                    <td className="px-4 py-3 text-body">{l.parent_name}</td>
                    <td className="px-4 py-3 text-body">{l.parent_whatsapp}</td>
                    <td className="px-4 py-3 text-body">{l.city}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {l.result?.avatar?.image && (
                          <img src={l.result.avatar.image} alt="" className="w-8 h-8 rounded-lg object-cover" />
                        )}
                        <span className="text-body text-xs">{l.result?.avatar?.name || '—'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {l.result?.top_zones?.[0] && (
                        <span className="inline-block text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: `${l.result.top_zones[0].color}1A`, color: l.result.top_zones[0].color }}>
                          {l.result.top_zones[0].name}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {l.result?.guidance_level && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                              style={{
                                background: l.result.guidance_level === 'High' ? '#FF5A00' : l.result.guidance_level === 'Medium' ? '#FFD600' : l.result.guidance_level === 'Light' ? '#0055FF' : '#F1F5F9',
                                color: l.result.guidance_level === 'Medium' ? '#0F172A' : l.result.guidance_level === 'Low' ? '#64748B' : '#fff',
                              }}>
                          {l.result.guidance_level}{l.result.uncertainty_count > 0 ? ` · ${l.result.uncertainty_count}` : ''}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">{l.cta_clicked ? <Pill ok>Yes</Pill> : <Pill>No</Pill>}</td>
                    <td className="px-4 py-3">{l.whatsapp_clicked ? <Pill ok>Yes</Pill> : <Pill>No</Pill>}</td>
                    <td className="px-4 py-3">
                      <Link to={`/admin/leads/${l.id}`} data-testid={`view-lead-${l.id}`} className="inline-flex items-center gap-1 text-[#0055FF] font-bold text-xs hover:underline">
                        View <ArrowRight className="w-3.5 h-3.5" strokeWidth={3} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="card-soft p-5 flex items-center gap-4">
      <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: `${color}1A`, color }}>
        <Icon strokeWidth={2.5} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-ink">{label}</div>
        <div className="font-display font-black text-ink text-3xl tracking-tighter">{value}</div>
      </div>
    </div>
  );
}

function Pill({ ok, children }) {
  return (
    <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${ok ? 'bg-[#00D95F]/15 text-[#0E7A37]' : 'bg-[#F1F5F9] text-muted-ink'}`}>{children}</span>
  );
}


const ZONE_COLOR_MAP = Object.values(ZONES).reduce((acc, z) => {
  acc[z.name] = z.color;
  return acc;
}, {});

function AnalyticsPanel({ analytics, range, onRangeChange }) {
  const series = analytics.series || [];
  const zones = analytics.zones || [];
  const total = analytics.total_in_window || 0;

  // Prettier labels (MMM DD) for the x-axis without pulling in date-fns.
  const chartData = series.map((s) => {
    const [, mm, dd] = (s.date || '').split('-');
    const monthShort = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][Number(mm) - 1] || '';
    return { ...s, label: monthShort && dd ? `${monthShort} ${dd}` : s.date };
  });

  const zoneData = zones.map((z) => ({
    ...z,
    color: ZONE_COLOR_MAP[z.zone] || '#0055FF',
  }));

  return (
    <div className="card-soft p-5" data-testid="admin-analytics-panel">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#0055FF]" strokeWidth={3} />
          <div>
            <div className="font-display font-extrabold text-ink">Completions over time</div>
            <div className="text-xs text-muted-ink mt-0.5">
              <span data-testid="analytics-total-in-window" className="font-bold text-ink">{total}</span> submission{total === 1 ? '' : 's'} in the last {range} days
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 bg-[#F1F5F9] rounded-full p-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              data-testid={`analytics-range-${d}`}
              onClick={() => onRangeChange(d)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${range === d ? 'bg-white text-[#0055FF] shadow-sm' : 'text-muted-ink hover:text-ink'}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 h-56" data-testid="analytics-daily-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
              <defs>
                <linearGradient id="cq-area" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0055FF" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#0055FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: '#64748B', fontSize: 11 }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={20} />
              <YAxis tick={{ fill: '#64748B', fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: '#0F172A', border: 'none', borderRadius: 12, color: '#fff', fontSize: 12 }}
                labelStyle={{ color: '#94A3B8', fontWeight: 700 }}
                itemStyle={{ color: '#fff' }}
              />
              <Area type="monotone" dataKey="count" stroke="#0055FF" strokeWidth={2.5} fill="url(#cq-area)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="h-56" data-testid="analytics-zones-chart">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-ink mb-2">
            Top career zones
          </div>
          {zoneData.length === 0 ? (
            <div className="h-40 flex items-center justify-center text-xs text-muted-ink">
              No data yet
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={zoneData} layout="vertical" margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
                <XAxis type="number" hide allowDecimals={false} />
                <YAxis type="category" dataKey="zone" tick={{ fill: '#0F172A', fontSize: 11, fontWeight: 700 }} tickLine={false} axisLine={false} width={82} />
                <Tooltip
                  contentStyle={{ background: '#0F172A', border: 'none', borderRadius: 12, color: '#fff', fontSize: 12 }}
                  labelStyle={{ color: '#94A3B8', fontWeight: 700 }}
                  itemStyle={{ color: '#fff' }}
                  cursor={{ fill: 'rgba(0,85,255,0.05)' }}
                />
                <Bar dataKey="count" radius={[6, 6, 6, 6]}>
                  {zoneData.map((entry, i) => (
                    <Cell key={`c-${i}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
